/**
 * Application Configuration
 * Centralized configuration for all application constants
 * 
 * This file contains all configurable values that were previously hardcoded
 * throughout the application. This makes the app more maintainable and
 * allows for easy environment-specific configurations.
 */

// User Roles and Permissions
// Based on actual backend role assignments:
// roleId 1 = Super Admin (Little platform admin)
// roleId 2 = Corporate Admin (admin of a specific corporate)
export const USER_ROLES = {
  SUPER_ADMIN: '1',
  CORPORATE_ADMIN: '2',
  ACCOUNT_MANAGER: '3',
  // Note: roleId 4 and 5 may exist for other user types
};

export const PERMISSIONS = {
  CREATE_CORPORATE: [USER_ROLES.SUPER_ADMIN],
  MANAGE_SETTINGS: [USER_ROLES.SUPER_ADMIN],
  MANAGE_PRICING: [USER_ROLES.SUPER_ADMIN],
  VIEW_ALL_CORPORATES: [USER_ROLES.SUPER_ADMIN, USER_ROLES.ACCOUNT_MANAGER],
  MANAGE_DRIVERS: [USER_ROLES.SUPER_ADMIN, USER_ROLES.CORPORATE_ADMIN],
  VIEW_TRIPS: [USER_ROLES.SUPER_ADMIN, USER_ROLES.CORPORATE_ADMIN, USER_ROLES.ACCOUNT_MANAGER],
  EXPORT_DATA: [USER_ROLES.SUPER_ADMIN, USER_ROLES.CORPORATE_ADMIN, USER_ROLES.ACCOUNT_MANAGER]
};

// Trip and Driver Types
export const TRIP_TYPES = {
  SHUTTLE: 'shuttle',
  PRIVATE: 'private',
  SCHEDULED: 'scheduled'
};

export const DRIVER_TYPES = {
  SHUTTLE: 'shuttle',
  PRIVATE: 'private',
  ALL: 'all'
};

export const TRIP_STATUS = {
  PENDING: 'pending',
  ACTIVE: 'active',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  ON_TRIP: 'on_trip'
};

export const DRIVER_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  ON_TRIP: 'on_trip',
  OFFLINE: 'offline'
};

// Date and Time Configurations
export const DATE_FORMATS = {
  DISPLAY: 'DD/MM/YYYY',
  API: 'YYYY-MM-DD',
  DATETIME_DISPLAY: 'DD/MM/YYYY HH:mm',
  DATETIME_API: 'YYYY-MM-DD HH:mm:ss',
  TIME_DISPLAY: 'HH:mm',
  TIME_API: 'HH:mm:ss'
};

export const TIME_PERIODS = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  CURRENT_WEEK: 'currentWeek',
  CURRENT_MONTH: 'currentMonth',
  LAST_MONTH: 'lastMonth',
  CURRENT_YEAR: 'currentYear',
  CUSTOM: 'custom'
};

// Map and Location Settings
export const MAP_CONFIG = {
  DEFAULT_CENTER: {
    lat: -1.2921,
    lng: 36.8219
  },
  DEFAULT_ZOOM: 11,
  MARKER_ICONS: {
    START_POINT: '../../Assets/Pin_icon_green50.png',
    END_POINT: '../../Assets/place_outline.png',
    STUDENT: '../../Assets/new_student_marker.png',
    CONNECTION: '../../Assets/start_location.png',
    DRIVER: '../../Assets/driver_marker.png'
  },
  POLLING_INTERVAL: 5000, // 5 seconds
  LOCATION_TIMEOUT: 10000, // 10 seconds
  MAX_ZOOM: 18,
  MIN_ZOOM: 8
};

// File Upload Settings
export const FILE_UPLOAD = {
  MAX_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_TYPES: {
    IMAGES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    DOCUMENTS: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    SPREADSHEETS: ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv']
  },
  UPLOAD_TIMEOUT: 30000 // 30 seconds
};

// Validation Rules
export const VALIDATION = {
  EMAIL_REGEX: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  PHONE_REGEX: /^[+]?[\d\s\-\(\)]{10,}$/,
  PASSWORD_MIN_LENGTH: 8,
  NAME_MIN_LENGTH: 2,
  NAME_MAX_LENGTH: 50,
  DESCRIPTION_MAX_LENGTH: 500,
  
  // Driver validation
  LICENSE_NUMBER_REGEX: /^[A-Z0-9]{5,20}$/,
  VEHICLE_NUMBER_REGEX: /^[A-Z0-9\-\s]{3,15}$/,
  
  // Corporate validation
  CORPORATE_CODE_REGEX: /^[A-Z0-9]{3,10}$/,
  GST_NUMBER_REGEX: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/
};

// Pagination Settings
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [5, 10, 25, 50, 100],
  MAX_PAGE_SIZE: 100,
  SHOW_SIZE_CHANGER: true,
  SHOW_QUICK_JUMPER: true
};

// Export Settings
export const EXPORT_CONFIG = {
  FORMATS: {
    CSV: 'csv',
    EXCEL: 'xlsx',
    PDF: 'pdf'
  },
  MAX_RECORDS: 10000,
  FILENAME_PREFIX: {
    TRIPS: 'trips_export',
    DRIVERS: 'drivers_export',
    CORPORATES: 'corporates_export',
    ROUTES: 'routes_export'
  },
  DATE_SUFFIX: true // Add date to filename
};

// Notification Settings
export const NOTIFICATIONS = {
  DURATION: {
    SUCCESS: 3000,
    INFO: 4000,
    WARNING: 5000,
    ERROR: 6000
  },
  POSITION: 'top-right',
  MAX_NOTIFICATIONS: 5,
  AUTO_DISMISS: true
};

// Cache Settings
export const CACHE_CONFIG = {
  DEFAULT_TTL: 5 * 60 * 1000, // 5 minutes
  DASHBOARD_TTL: 2 * 60 * 1000, // 2 minutes
  STATIC_DATA_TTL: 30 * 60 * 1000, // 30 minutes
  USER_DATA_TTL: 10 * 60 * 1000, // 10 minutes
  MAX_CACHE_SIZE: 100, // Maximum number of cached items
  STORAGE_KEY_PREFIX: 'corporate_portal_'
};

// API Configuration
export const API_CONFIG = {
  TIMEOUT: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second
  RATE_LIMIT: {
    MAX_REQUESTS: 100,
    WINDOW_MS: 60000 // 1 minute
  },
  HEADERS: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
};

// Feature Flags
export const FEATURES = {
  ENABLE_DARK_MODE: false,
  ENABLE_REAL_TIME_TRACKING: true,
  ENABLE_BULK_OPERATIONS: true,
  ENABLE_ADVANCED_FILTERS: true,
  ENABLE_EXPORT_SCHEDULING: false,
  ENABLE_MOBILE_APP_INTEGRATION: true,
  ENABLE_ANALYTICS_DASHBOARD: false,
  ENABLE_MULTI_LANGUAGE: false
};

// UI Configuration
export const UI_CONFIG = {
  SIDEBAR: {
    DEFAULT_COLLAPSED: false,
    REMEMBER_STATE: true,
    ANIMATION_DURATION: 300
  },
  
  TABLE: {
    STICKY_HEADER: true,
    SHOW_BORDERS: true,
    ALTERNATE_ROW_COLORS: true,
    COMPACT_MODE: false
  },
  
  FORMS: {
    AUTO_SAVE: false,
    AUTO_SAVE_INTERVAL: 30000, // 30 seconds
    SHOW_REQUIRED_INDICATORS: true,
    VALIDATE_ON_BLUR: true
  },
  
  MODALS: {
    CLOSE_ON_ESCAPE: true,
    CLOSE_ON_BACKDROP_CLICK: true,
    SHOW_CLOSE_BUTTON: true,
    ANIMATION_DURATION: 200
  }
};

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  SERVER_ERROR: 'Server error. Please try again later.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  FORBIDDEN: 'Access denied. Please contact your administrator.',
  NOT_FOUND: 'The requested resource was not found.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  TIMEOUT_ERROR: 'Request timed out. Please try again.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
  
  // Specific errors
  INVALID_EMAIL: 'Please enter a valid email address.',
  INVALID_PHONE: 'Please enter a valid phone number.',
  PASSWORD_TOO_SHORT: `Password must be at least ${VALIDATION.PASSWORD_MIN_LENGTH} characters long.`,
  REQUIRED_FIELD: 'This field is required.',
  FILE_TOO_LARGE: `File size must be less than ${FILE_UPLOAD.MAX_SIZE / (1024 * 1024)}MB.`,
  INVALID_FILE_TYPE: 'Invalid file type. Please select a valid file.'
};

// Success Messages
export const SUCCESS_MESSAGES = {
  SAVE_SUCCESS: 'Data saved successfully.',
  UPDATE_SUCCESS: 'Data updated successfully.',
  DELETE_SUCCESS: 'Data deleted successfully.',
  UPLOAD_SUCCESS: 'File uploaded successfully.',
  EXPORT_SUCCESS: 'Data exported successfully.',
  EMAIL_SENT: 'Email sent successfully.',
  PASSWORD_CHANGED: 'Password changed successfully.',
  PROFILE_UPDATED: 'Profile updated successfully.'
};

// Local Storage Keys
export const STORAGE_KEYS = {
  USER_TOKEN: 'authToken',
  USER_DATA: 'userData',
  USER_PREFERENCES: 'userPreferences',
  SIDEBAR_STATE: 'sidebarCollapsed',
  THEME_MODE: 'themeMode',
  LANGUAGE: 'selectedLanguage',
  RECENT_SEARCHES: 'recentSearches',
  DASHBOARD_LAYOUT: 'dashboardLayout'
};

// Environment-specific configurations
export const ENV_CONFIG = {
  DEVELOPMENT: {
    API_BASE_URL: 'http://localhost:3001',
    ENABLE_LOGGING: true,
    ENABLE_DEBUG_TOOLS: true
  },
  
  STAGING: {
    API_BASE_URL: 'https://staging-api.example.com',
    ENABLE_LOGGING: true,
    ENABLE_DEBUG_TOOLS: false
  },
  
  PRODUCTION: {
    API_BASE_URL: 'https://api.example.com',
    ENABLE_LOGGING: false,
    ENABLE_DEBUG_TOOLS: false
  }
};

// Get current environment configuration
export const getCurrentEnvConfig = () => {
  const env = process.env.NODE_ENV || 'development';
  return ENV_CONFIG[env.toUpperCase()] || ENV_CONFIG.DEVELOPMENT;
};

// Export all configurations as a single object
export const APP_CONFIG = {
  userRoles: USER_ROLES,
  permissions: PERMISSIONS,
  tripTypes: TRIP_TYPES,
  driverTypes: DRIVER_TYPES,
  tripStatus: TRIP_STATUS,
  driverStatus: DRIVER_STATUS,
  dateFormats: DATE_FORMATS,
  timePeriods: TIME_PERIODS,
  mapConfig: MAP_CONFIG,
  fileUpload: FILE_UPLOAD,
  validation: VALIDATION,
  pagination: PAGINATION,
  exportConfig: EXPORT_CONFIG,
  notifications: NOTIFICATIONS,
  cacheConfig: CACHE_CONFIG,
  apiConfig: API_CONFIG,
  features: FEATURES,
  uiConfig: UI_CONFIG,
  errorMessages: ERROR_MESSAGES,
  successMessages: SUCCESS_MESSAGES,
  storageKeys: STORAGE_KEYS,
  envConfig: getCurrentEnvConfig()
};

export default APP_CONFIG;
